import { Users, Heart, Video, DollarSign, ShoppingBag, Eye, TrendingUp, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { ArsenalCreator } from '@/types/arsenalCreators';
import { CreatorAvatarImage } from '@/components/arsenal/CreatorAvatarImage';

const COUNTRY_NAMES: Record<string, string> = {
  BR: 'Brasil', US: 'Estados Unidos', GB: 'Reino Unido', ES: 'Espanha', DE: 'Alemanha', FR: 'França',
  IT: 'Itália', PT: 'Portugal', MX: 'México', AR: 'Argentina', CO: 'Colômbia', CL: 'Chile',
  CA: 'Canadá', AU: 'Austrália', JP: 'Japão', KR: 'Coreia do Sul', CN: 'China', IN: 'Índia',
  ID: 'Indonésia', TH: 'Tailândia', VN: 'Vietnã', MY: 'Malásia', PH: 'Filipinas', SG: 'Singapura',
  SA: 'Arábia Saudita', AE: 'Emirados Árabes', NG: 'Nigéria', ZA: 'África do Sul',
};

const COUNTRY_CURRENCY: Record<string, string> = {
  US: '$', BR: 'R$', GB: '£', EU: '€', DE: '€', FR: '€', ES: '€', IT: '€', PT: '€',
  JP: '¥', KR: '₩', CN: '¥', IN: '₹', MX: 'MX$', CA: 'CA$', AU: 'A$',
};

function getCurrencySymbol(country: string): string {
  return COUNTRY_CURRENCY[country] || '$';
}

function getCountryName(country: string): string {
  return COUNTRY_NAMES[country] || country;
}

export function formatMetricCount(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

interface CreatorCardProps {
  creator: ArsenalCreator;
  isAdmin?: boolean;
  /** Admin-only: extra action buttons rendered in the footer */
  adminActions?: React.ReactNode;
  /** Admin-only: date stamps rendered in the footer */
  adminDates?: React.ReactNode;
}

export function CreatorCard({ creator: c, isAdmin = false, adminActions, adminDates }: CreatorCardProps) {
  return (
    <div className="group p-3 md:p-4 rounded-xl transition-all duration-200 flex flex-col h-full forja-card hover:-translate-y-[2px] hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 border-b-[3px] border-b-primary/40 relative">

      {/* Header with avatar */}
      <div className="flex items-start gap-2 md:gap-3 mb-2 md:mb-3">
        <CreatorAvatarImage creator={c} size="md" fallback="icon" />

        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-xs md:text-sm truncate">{c.nickname || c.username}</h3>
          <p className="text-[10px] md:text-xs text-muted-foreground truncate">
            @{c.username}
          </p>
          <p className="text-[9px] md:text-[10px] text-muted-foreground/70 line-clamp-2 leading-tight mt-0.5 min-h-[24px] md:min-h-[28px]">
            {c.bio || '\u00A0'}
          </p>
        </div>
      </div>

      {/* Stats row */}
      <div className="flex flex-wrap items-center gap-2 md:gap-3 text-[10px] md:text-xs text-muted-foreground mb-2 md:mb-3">
        <span className="flex items-center gap-1">
          <Users className="w-3 md:w-3.5 h-3 md:h-3.5" />
          {formatMetricCount(c.follower_count)}
        </span>
        {c.heart_count > 0 && (
          <span className="flex items-center gap-1">
            <Heart className="w-3 md:w-3.5 h-3 md:h-3.5 text-pink-500" />
            {formatMetricCount(c.heart_count)}
          </span>
        )}
        {c.video_count > 0 && (
          <span className="flex items-center gap-1">
            <Video className="w-3 md:w-3.5 h-3 md:h-3.5" />
            {c.video_count}
          </span>
        )}
        <Badge variant="outline" className="ml-auto text-[9px] md:text-[10px] px-1 md:px-1.5 py-0 h-4 md:h-5 text-muted-foreground border-muted-foreground/30 bg-muted/30">
          {getCountryName(c.country)}
        </Badge>
        <Badge variant="outline" className="text-[9px] md:text-[10px] px-1 md:px-1.5 py-0 h-4 md:h-5 text-amber-400 border-amber-500/30 bg-amber-500/5">
          ⭐ Nível {c.creator_level}
        </Badge>
      </div>

      {/* Spacer */}
      <div className="flex-grow" />

      {/* Metrics footer */}
      <div className="flex flex-col gap-1.5 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 mt-auto">
        {c.gmv_cents > 0 && (
          <div className="flex items-center gap-1.5">
            <DollarSign className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-bold text-amber-400 tabular-nums">
              {getCurrencySymbol(c.country)} {formatMetricCount(c.gmv_cents / 100)} GMV
            </span>
          </div>
        )}
        {c.units_sold > 0 && (
          <div className="flex items-center gap-1.5">
            <ShoppingBag className="w-3.5 h-3.5 text-emerald-400/70" />
            <span className="text-xs text-emerald-400/70 tabular-nums">
              {formatMetricCount(c.units_sold)} unidades vendidas
            </span>
          </div>
        )}
        {c.avg_views > 0 && (
          <div className="flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5 text-emerald-400/70" />
            <span className="text-xs text-emerald-400/70 tabular-nums">
              {formatMetricCount(c.avg_views)} média views vídeos
            </span>
          </div>
        )}
        {c.engagement_rate > 0 && (
          <div className="flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-400/70" />
            <span className="text-xs text-emerald-400/70 tabular-nums">
              {c.engagement_rate}% taxa de engajamento
            </span>
          </div>
        )}
      </div>

      {/* Footer: dates + actions */}
      <div className="mt-2 flex items-end justify-between">
        {isAdmin && adminDates ? (
          <div className="flex flex-col gap-0.5 text-[9px] text-muted-foreground/60">
            {adminDates}
          </div>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => window.open(`https://www.tiktok.com/@${c.username}`, '_blank')}
            className="p-1 rounded-md hover:bg-primary/10"
            title="Ver perfil no TikTok"
          >
            <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
          {isAdmin && adminActions}
        </div>
      </div>
    </div>
  );
}
